import angular from 'angular';

import { BursonOrderLineModel }      from './models/burson.order-line';
import { BursonOrderOptionsModel }   from './models/burson.order-options';
import { BursonOrderStatusModel }    from './models/burson.order-status';
import { BursonPortalSettingsModel } from '../../app-lite/burson/models/burson.portal-settings';
import { BursonSettingsModel }       from './models/burson.settings';
import { CompanyIntegrationsModel }  from '../../app-lite/companies/models/company.integrations';
import { CustomerModel }             from '../../app-lite/customers/models/customer';
import { PurchaseOrderModel }        from '../purchase-orders/models/purchase-order';
import { UserModel }                 from '../../app-lite/users/models/user';
import { VehicleModel }              from '../../app-lite/vehicles/models/vehicle';
import { VendorModel }               from '../../app-lite/vendors/models/vendor';

import ResolveBursonOrderFailuresHtml from './burson.order.resolve-failures.html';

export class BursonServiceAjs {
  constructor (
    private $uibModal    : ng.ui.bootstrap.IModalService,
    private $q           : ng.IQService,
    private API          : any,
    private blockUI      : ng.blockUI.BlockUIService,
    private sessionObjAPI: any,
    private workshopAPI  : any
  ) {}

  activate () : angular.IPromise<boolean> {
    return this.workshopAPI.get('/company_integration/add_burson_ezyparts');
  };

  checkOrderStatus ( order : PurchaseOrderModel ) : angular.IPromise<BursonOrderStatusModel> {
    const self = this;

    return self.workshopAPI.get('/burson_ezyparts/get_inventory_status', order.id)
    .then(( status : BursonOrderStatusModel) => {
      /**
        * Instead of being an empty array, these are null sometimes.
        * to prevent length check errors, i default to empty arrays.
        * */
      status.failed_items  = status.failed_items || [];
      status.success_items = status.success_items || [];

      return status;
    });
  };

  deactivate () : angular.IPromise<boolean> {
    return this.workshopAPI.get('/company_integration/remove_burson_ezyparts');
  };

  getReturnUrl (type, record) : string {
    const self = this;

    return record.vehicle_id ?
      `${ self.sessionObjAPI.base().origin }workshop.html#/${ record.customer_id }/${ record.vehicle_id }/${ type }/${ record.id }` :
      `${ self.sessionObjAPI.base().origin }workshop.html#/${ record.customer_id }/${ type }/${ record.id }`;
  };

  getSessionSettings () : BursonSettingsModel {
    return this.sessionObjAPI.company().burson_ezyparts_settings;
  }

  getSettings () : angular.IPromise<CompanyIntegrationsModel> {
    const self = this;

    return self.workshopAPI.get('/company_integrations');
  };

  getUrl (type = 'invoice', record: any = {}) : angular.IPromise<string> {
    const self                     = this;
    let customer : CustomerModel = null;

    const settings : BursonPortalSettingsModel = {
      fields: {
        quoteUrl : `${ this.API.url }/${ type }s/add_item_from_burson_ezyparts/${ record.id }/${ self.sessionObjAPI.sessionID() }`,
        returnUrl: self.getReturnUrl(type, record)
      },
      url: ''
    };

    return self.workshopAPI.get('/company_integrations')
    .then(( integrations : CompanyIntegrationsModel ) => {
      settings.fields.accountId =  integrations.burson_ezyparts_account_number;

      return self.workshopAPI.get('/burson_ezyparts/return_quote_url');
    })
    .then(( url : string ) => {
      settings.url = url;

      return url;
    })
    .then(() => self.workshopAPI.get('/customer', record.customer_id))
    .then(( response : CustomerModel ) => {
      customer = response;

      return response;
    })
    .then(() => self.workshopAPI.get('/company_integration/burson_ezyparts_credentials', self.sessionObjAPI.user().id))
    .then(( user : UserModel ) => {
      settings.fields.password = user.burson_ezyparts_password;
      settings.fields.username = user.burson_ezyparts_username;

      return user;
    })
    .then(() => record.vehicle_id ? self.workshopAPI.get('/vehicle', record.vehicle_id) : self.$q.when({}))
    .then((vehicle : VehicleModel) => {
      if (vehicle.id) {
        settings.fields.engine        = vehicle.engine_code;
        settings.fields.make          = vehicle.make;
        settings.fields.model         = vehicle.model;
        settings.fields.regoNumber    = vehicle.plate_number;
        settings.fields.seriesChassis = vehicle.chassis;
        settings.fields.state         = vehicle.plate_state || customer.state || self.sessionObjAPI.company().state;
        settings.fields.vehicleId     = vehicle.bursons_vehicle_id;
        settings.fields.year          = vehicle.build_date;
        settings.fields.isRegoSearch  = !!(vehicle.plate_number && settings.fields.state);
      }

      self.sessionObjAPI.setKey('bursonAppSettings', settings);

      return self.$q.when('/burson.html');
    });
  };

  getVendorName () : string {
    const settings : BursonSettingsModel = this.sessionObjAPI.company().burson_ezyparts_settings;

    return settings.burson_ezyparts_vendor_name;
  };

  isOrderInvalid ( failures : Array<BursonOrderLineModel> ) : boolean {
    let isInvalid = false;

    angular.forEach(failures, failure => {
      if (failure.code === 'NBP') {
        isInvalid = true;
      }
    });

    return isInvalid;
  };

  isUpgraded ( settings : CompanyIntegrationsModel ) : boolean {
    const session : BursonSettingsModel = this.sessionObjAPI.company().burson_ezyparts_settings;

    return settings ? settings.burson_ezyparts_v2 : session.burson_ezyparts_v2;
  };

  isVendor ( vendor : VendorModel ) : boolean {
    const settings : BursonSettingsModel = this.getSessionSettings();

    return settings.burson_ezyparts_vendor_id === vendor.id;
  };

  removeFailedItems ( order : PurchaseOrderModel, failures : Array<BursonOrderLineModel> ) : angular.IPromise<boolean> {
    const self = this;

    return self.workshopAPI.post('/burson_ezyparts/remove_failed_items_from_burson_order', {
      failed_items: failures,
      order_id    : order.id
    });
  }

  resolveOrderFailures ( order : PurchaseOrderModel, failures : Array<BursonOrderLineModel> ) : angular.IPromise<any> {
    const self = this;

    return self.$uibModal.open({
      controller: 'ResolveBursonOrderFailuresCtrl',
      resolve   : {
        failures: () => failures,
        order   : () => order
      },
      templateUrl: ResolveBursonOrderFailuresHtml,
      windowClass: 'lg-modal'
    }).result;
  };

  saveSettings ( settings : CompanyIntegrationsModel, user : UserModel ) : angular.IPromise<CompanyIntegrationsModel> {
    const self = this;

    // save terminal id
    return self.workshopAPI.patch('/user', user, 'user')
    // Save settings
    .then(() => self.workshopAPI.patch('/company_integrations', settings, 'company_integration'));
  };

  sendOrder (
    order   : PurchaseOrderModel,
    options : BursonOrderOptionsModel     = { force: false }
  ) : angular.IPromise<PurchaseOrderModel> {
    const self     = this;

    return self.$q.when()
    .then(() => {
      return options.failures && options.failures.length
        ? self.removeFailedItems(order, options.failures)
          .then(() => order)
        : self.$q.when(order);
    })
    .then(() => {
      return options.force
        ? self.$q.when({ failed_items: [] })
        : self.checkOrderStatus(order);
    })
    .then(( status : BursonOrderStatusModel ) => {
      if (status.failed_items && status.failed_items.length && !options.force) {
        ( this.blockUI.instances as unknown as Array<any> )
        .forEach(instance => this.blockUI.instances.get(instance._id).stop());

        return self.resolveOrderFailures(order, status.failed_items);
      }

      return self.workshopAPI.post(`/purchase_orders/send_order_to_burson_ezyparts/${ order.id }`)
      .then(( result : any ) => {
        if (result.failOrderLines && result.failOrderLines.length) {
          return self.$q.reject(result.failOrderLines[0].reason);
        }

        return self.$q.when(order);
      });
    });
  };
}
